import React, { useState } from 'react';
import {
    Box,
    Button,
    Input,
    FormControl,
    FormLabel,
    Text,
    VStack,
    SimpleGrid,
    Badge,
    useToast,
    Heading,
    HStack,
    Divider,
    Spinner,
    Flex,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleInfo,
    faPlus,
    faTag,
    faCoins,
    faPercent,
} from '@fortawesome/free-solid-svg-icons';
import { createPromoCode, getUsedPromoCodesPerUser } from '../../services/affiliate';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

const inputStyles = {
    bg: 'gray.50',
    borderColor: 'gray.200',
    borderRadius: 'md',
    _hover: { borderColor: 'teal.300' },
    _focusVisible: {
        borderColor: 'teal.400',
        boxShadow: '0 0 0 1px var(--chakra-colors-teal-400)',
    },
};

const CommissionInfo = () => {
    const { t } = useTranslation('common');

    return (
        <Box
            bg="white"
            borderWidth="1px"
            borderColor="teal.100"
            borderRadius="xl"
            p={{ base: 4, md: 5 }}
            boxShadow="sm"
            borderLeftWidth="4px"
            borderLeftColor="teal.400"
        >
            <HStack spacing={3} align="flex-start">
                <Flex
                    align="center"
                    justify="center"
                    w="36px"
                    h="36px"
                    borderRadius="full"
                    bg="teal.50"
                    color="teal.600"
                    flexShrink={0}
                >
                    <FontAwesomeIcon icon={faCircleInfo} />
                </Flex>
                <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={1}>
                        {t('affiliateCommissionPolicyTitle')}
                    </Text>
                    <Text fontSize="sm" color="gray.600" lineHeight="tall">
                        {t('affiliateCommissionPolicyBody')}
                    </Text>
                </Box>
            </HStack>
        </Box>
    );
};

const PromoCodeForm = () => {
    const { t } = useTranslation('common');
    const [promoCode, setPromoCode] = useState({
        code: '',
        active: true,
        discount_amount: 0.05,
    });
    const toast = useToast();

    const mutation = useMutation({
        mutationFn: createPromoCode,
        onSuccess: (data) => {
            toast({
                title: t('affiliatePromoCodeCreated', { code: data.data.code }),
                status: 'success',
            });
            window.location.reload();
        },
        onError: () => {
            toast({
                title: t('affiliatePromoCodeError'),
                status: 'error',
            });
        },
    });

    return (
        <Box
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
            p={{ base: 5, md: 6 }}
            boxShadow="sm"
        >
            <HStack spacing={2} mb={4}>
                <Flex
                    align="center"
                    justify="center"
                    w="32px"
                    h="32px"
                    borderRadius="md"
                    bg="teal.50"
                    color="teal.600"
                >
                    <FontAwesomeIcon icon={faPlus} size="sm" />
                </Flex>
                <Heading size="md" color="gray.800">
                    {t('affiliateCreatePromoCode')}
                </Heading>
            </HStack>
            <FormControl id="promo-code" isRequired>
                <FormLabel fontSize="sm" color="gray.600" mb={1}>
                    {t('affiliatePromoCodeName')}
                </FormLabel>
                <Input
                    type="text"
                    onChange={(e) => {
                        setPromoCode((prev) => ({
                            ...prev,
                            code: e.target.value,
                        }));
                    }}
                    placeholder={t('affiliatePromoCodePlaceholder')}
                    {...inputStyles}
                />
            </FormControl>
            <Button
                mt={4}
                colorScheme="teal"
                leftIcon={<FontAwesomeIcon icon={faTag} />}
                onClick={() => mutation.mutate(promoCode)}
                isLoading={mutation.isLoading}
                borderRadius="md"
                px={6}
            >
                {t('affiliateCreatePromoCode')}
            </Button>
        </Box>
    );
};

const PromoCodesList = ({ promoCodes }) => {
    const { t } = useTranslation('common');

    if (!Array.isArray(promoCodes)) {
        return (
            <Flex justify="center" py={10}>
                <Spinner size="lg" color="teal.500" thickness="3px" />
            </Flex>
        );
    }

    const totalCommission = promoCodes.reduce(
        (sum, code) => sum + code.totalCommission,
        0
    );

    return (
        <Box>
            <Heading size="md" color="gray.800" mb={4}>
                {t('affiliateYourPromoCodes')}
            </Heading>

            <Box
                bg="linear-gradient(135deg, var(--chakra-colors-teal-500) 0%, var(--chakra-colors-teal-600) 100%)"
                borderRadius="xl"
                p={{ base: 5, md: 6 }}
                mb={6}
                color="white"
                boxShadow="md"
            >
                <HStack spacing={3} align="center">
                    <Flex
                        align="center"
                        justify="center"
                        w="44px"
                        h="44px"
                        borderRadius="full"
                        bg="whiteAlpha.300"
                    >
                        <FontAwesomeIcon icon={faCoins} size="lg" />
                    </Flex>
                    <Box>
                        <Text fontSize="sm" opacity={0.9} fontWeight="medium">
                            {t('affiliateTotalEarnings')}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" lineHeight="short">
                            EUR {totalCommission.toFixed(2)}
                        </Text>
                    </Box>
                </HStack>
            </Box>

            {promoCodes.length === 0 ? (
                <Box
                    textAlign="center"
                    py={10}
                    px={4}
                    bg="gray.50"
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.200"
                >
                    <Text color="gray.500">{t('affiliateNoPromoCodesYet')}</Text>
                </Box>
            ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    {promoCodes.map((promoCode) => (
                        <Box
                            key={promoCode.id}
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="xl"
                            p={5}
                            boxShadow="sm"
                            transition="all 0.2s"
                            _hover={{
                                borderColor: 'teal.200',
                                boxShadow: 'md',
                                transform: 'translateY(-2px)',
                            }}
                        >
                            <HStack justify="space-between" align="flex-start" mb={3}>
                                <HStack spacing={2}>
                                    <Flex
                                        align="center"
                                        justify="center"
                                        w="28px"
                                        h="28px"
                                        borderRadius="md"
                                        bg="teal.50"
                                        color="teal.600"
                                    >
                                        <FontAwesomeIcon icon={faPercent} size="sm" />
                                    </Flex>
                                    <Text fontSize="lg" fontWeight="bold" color="gray.800">
                                        {promoCode.code}
                                    </Text>
                                </HStack>
                                <Badge
                                    colorScheme={promoCode.active ? 'teal' : 'gray'}
                                    variant="subtle"
                                    borderRadius="full"
                                    px={3}
                                >
                                    {promoCode.active
                                        ? t('affiliateActive')
                                        : t('affiliateInactive')}
                                </Badge>
                            </HStack>

                            <Divider mb={3} />

                            <VStack align="stretch" spacing={2} fontSize="sm">
                                <HStack justify="space-between">
                                    <Text color="gray.500">{t('affiliateDiscount')}</Text>
                                    <Text fontWeight="medium" color="gray.800">
                                        {parseFloat(promoCode.discountAmount) * 100}%
                                    </Text>
                                </HStack>
                                <HStack justify="space-between">
                                    <Text color="gray.500">{t('affiliateUsage')}</Text>
                                    <Badge
                                        colorScheme={promoCode.usageCount > 0 ? 'green' : 'gray'}
                                        variant="subtle"
                                    >
                                        {promoCode.usageCount}{' '}
                                        {promoCode.usageCount === 1
                                            ? t('affiliateUsageTime')
                                            : t('affiliateUsageTimes')}
                                    </Badge>
                                </HStack>
                                <HStack justify="space-between" pt={1}>
                                    <Text color="gray.500">
                                        {t('affiliateCommissionEarned')}
                                    </Text>
                                    <Text fontWeight="bold" color="teal.600">
                                        EUR {promoCode.totalCommission.toFixed(2)}
                                    </Text>
                                </HStack>
                            </VStack>
                        </Box>
                    ))}
                </SimpleGrid>
            )}
        </Box>
    );
};

const PromoCodesPage = () => {
    const { t } = useTranslation('common');

    const { data: promoCodes } = useQuery({
        queryKey: ['getPromoCodes'],
        queryFn: () => getUsedPromoCodesPerUser(),
        refetchOnWindowFocus: false,
        select: (data) => data.data,
        enabled: true,
    });

    return (
        <Box bg="gray.50" minH="calc(100vh - 80px)" py={{ base: 6, md: 10 }}>
            <Box maxW="900px" mx="auto" px={{ base: 4, md: 6 }}>
                <Box mb={8}>
                    <Heading size="lg" color="teal.600" mb={2}>
                        {t('affiliate')}
                    </Heading>
                    <Text color="gray.600" fontSize="sm">
                        {t('affiliateDashboardSubtitle')}
                    </Text>
                </Box>

                <VStack spacing={6} align="stretch">
                    <CommissionInfo />
                    <PromoCodeForm />
                    <PromoCodesList promoCodes={promoCodes} />
                </VStack>
            </Box>
        </Box>
    );
};

export default PromoCodesPage;
